import React, { useState, useEffect, useRef } from "react";
import "./TeamTree.css";

const CustomNode = ({ name, role }) => {
  return (
    <div className="tree-node">
      <div className="node-content">
        <h4>{name}</h4>
        {role && <p>{role}</p>}
      </div>
    </div>
  );
};

const TeamTree = ({
  data,
  onDateChange,
  noDataMessage,
  treeLoading,
  defaultDate
}) => {
  const [expandedNodes, setExpandedNodes] = useState({});
  const [zoom, setZoom] = useState(1);

  // ⭐ TEMP DATE (no API call until Apply clicked)
  const [tempDate, setTempDate] = useState("");

  const scrollWrapperRef = useRef(null);
  const rootNodeRef = useRef(null);

  // ⭐ Set default date (yesterday) from parent
  useEffect(() => {
    if (defaultDate) {
      setTempDate(defaultDate);
    }
  }, [defaultDate]);

  // ⭐ Center Tree
  const centerTree = () => {
    if (scrollWrapperRef.current && rootNodeRef.current) {
      const wrapper = scrollWrapperRef.current;
      const root = rootNodeRef.current;

      const wrapperCenter = wrapper.offsetWidth / 2;
      const nodeCenter = root.offsetLeft + root.offsetWidth / 2;

      wrapper.scrollLeft = nodeCenter - wrapperCenter;
    }
  };

  useEffect(() => {
    centerTree();
  }, [data]);

  useEffect(() => {
    centerTree();
  }, [zoom]);

  const toggleNode = (id) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
    setTimeout(centerTree, 50);
  };

  const renderTree = (node, isRoot = false) => {
    const isExpanded = expandedNodes[node.id] ?? true;

    return (
      <li key={node.id} ref={isRoot ? rootNodeRef : null}>
        <CustomNode name={node.name} role={node.role} />

        {node.children && node.children.length > 0 && isExpanded && (
          <ul>
            {node.children.map((child) => renderTree(child))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className="tree-wrapper">
      <h2 className="tree-title">Team Hierarchy</h2>

      {/* ⭐ DATE FILTER WITH APPLY BUTTON */}
      <div className="tree-date-filter">
        <label>Select date to view hierarchy of particular day:</label>

        <input
          type="date"
          value={tempDate}
          onChange={(e) => {
            setTempDate(e.target.value); // ONLY update local state
          }}
        />

        <button
          className="apply-btn"
          disabled={treeLoading}
          onClick={() => {
            if (!tempDate) return;

            if (onDateChange) {
              onDateChange(tempDate); // API hit ONLY here
            }
          }}
        >
          {treeLoading ? "Loading..." : "Apply Filter"}
        </button>
      </div>

      {/* ⭐ LOADING MESSAGE */}
      {treeLoading && (
        <div
          style={{
            textAlign: "center",
            padding: "1rem",
            fontSize: "1.2rem",
            color: "#444",
          }}
        >
          ⏳ Loading data…
        </div>
      )}

      {/* ⭐ NO DATA MESSAGE */}
      {!treeLoading && noDataMessage && (
        <div
          style={{
            textAlign: "center",
            padding: "1rem",
            fontSize: "1.2rem",
            color: "#666",
          }}
        >
          {noDataMessage}
        </div>
      )}

      {/* ⭐ ZOOM CONTROLS */}
      <div className="zoom-controls">
        <button onClick={() => setZoom((z) => Math.min(z + 0.1, 2))}>+</button>
        <button onClick={() => setZoom((z) => Math.max(z - 0.1, 0.2))}>−</button>
        <button onClick={() => setZoom(1)}>Reset</button>
      </div>

      {/* ⭐ TREE */}
      {!treeLoading && (
        <div className="scrollbar-wrapper" ref={scrollWrapperRef}>
          <div
            className="tree-container"
            style={{ transform: `scale(${zoom})` }}
          >
            {data ? (
              <ul className="tree">
                {Array.isArray(data)
                  ? data.map((node) => renderTree(node, true))
                  : renderTree(data, true)}
              </ul>
            ) : (
              <p style={{ textAlign: "center", color: "#555" }}>
                No tree data loaded yet.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamTree;
